import { Box, ErrorBoundary, HStack, VStack } from '@genial/components';
import React from 'react';

import {
  reportGeneratorListLayout,
  reportGeneratorTableFrameInset,
  reportGeneratorTableHead,
  reportGeneratorTableHorizontalPadding,
  reportGeneratorTableListPanelPadding,
} from '../../../constants/table';
import { reportGeneratorTheme } from '../../../constants/theme';

import { DocumentsTableList } from './shared/DocumentsTableList';
import { DocumentsTableSkeletonList } from './shared/DocumentsTableSkeletonList';
import { ReportGeneratorTableHeadItem } from './shared/ReportGeneratorTableHeadItem';
import { ReportGeneratorListingProps } from './types';

interface DocumentsTableTableLayoutProps {
  listing: ReportGeneratorListingProps;
}

const ReportGeneratorTableHead: React.FC<{
  orderBy: ReportGeneratorListingProps['orderBy'];
  onOrderBy: ReportGeneratorListingProps['onOrderBy'];
}> = ({ orderBy, onOrderBy }) => (
  <Box width="full" px={reportGeneratorTableListPanelPadding}>
    <Box width="full" px={reportGeneratorTableHorizontalPadding} py="inline.xxxs">
      <HStack width="full" alignItems="center" justifyContent="flex-start">
        {reportGeneratorTableHead.map((column) => (
          <ReportGeneratorTableHeadItem
            key={column.title}
            title={column.title}
            flex={column.flex || 1}
            value={column.orderValue as string | undefined}
            orderBy={orderBy}
            onPress={() => column.orderValue && onOrderBy(column.orderValue as string)}
          />
        ))}
      </HStack>
    </Box>
  </Box>
);

export const DocumentsTableTableLayout: React.FC<DocumentsTableTableLayoutProps> = ({
  listing,
}) => {
  const {
    items,
    loading,
    error,
    orderBy,
    isLoadingMore,
    appliedFiltersCount,
    onRefresh,
    onEndReached,
    onOrderBy,
    onDownload,
    onRetry,
  } = listing;

  const isListLoading = loading && !isLoadingMore;

  if (error) {
    return (
      <ErrorBoundary
        onRefresh={onRefresh}
        errorTitle="Não foi possível carregar o histórico"
        errorMessage="Tente novamente em alguns instantes."
      />
    );
  }

  return (
    <Box
      width="full"
      bg={reportGeneratorTheme.table.historyBg}
      borderRadius={reportGeneratorTheme.table.frameBorderRadius}
      p={reportGeneratorTableFrameInset}>
      <VStack width="full" space={reportGeneratorTableFrameInset}>
        <ReportGeneratorTableHead orderBy={orderBy} onOrderBy={onOrderBy} />

        <Box
          width="full"
          bg={reportGeneratorTheme.table.rowBg}
          borderRadius={reportGeneratorTheme.table.listPanelBorderRadius}
          borderWidth={1}
          borderColor={reportGeneratorTheme.table.listPanelBorderColor}
          px={reportGeneratorTableListPanelPadding}
          pb={reportGeneratorTableListPanelPadding}>
          <Box h={reportGeneratorTableListPanelPadding} width="full" />
          <Box width="full" maxH={`${reportGeneratorListLayout.desktopMaxHeight}px`}>
            {isListLoading ? (
              <DocumentsTableSkeletonList itemSeparatorSpace={reportGeneratorTheme.table.itemGap} />
            ) : (
              <DocumentsTableList
                items={items}
                isLoadingMore={isLoadingMore}
                scrollEnabled
                onEndReached={onEndReached}
                listStyle={{
                  flexGrow: 1,
                  maxHeight: reportGeneratorListLayout.desktopMaxHeight,
                }}
                listContentPadding={false}
                listBackground={false}
                itemSeparatorSpace={reportGeneratorTheme.table.itemGap}
                appliedFiltersCount={appliedFiltersCount}
                onDownload={onDownload}
                onRetry={onRetry}
              />
            )}
          </Box>
        </Box>
      </VStack>
    </Box>
  );
};
